import React from 'react';

import { BankIcon, BuildingIcon, DocumentCopyIcon } from '@sharedCR/coreDesignSystem/icons';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { getConditionalArray } from 'utils/data';

import ManufacturedHomeContactsBanner from './Content/components/ManufacturedHomeContactsBanner';
import { fullWidthWrapperStyles, GROUP_TITLE_CLASS_NAME } from './EditDrawer.styles';

export const getBrokerFieldIds = prefix => ({
  attendantCompanyId: `${prefix}AttendantCompanyId`,
  attendantCompanyLocationId: `${prefix}AttendantCompanyLocationId`,
  name: `${prefix}Name`,
  streetAddress: `${prefix}StreetAddress`,
  city: `${prefix}City`,
  state: `${prefix}State`,
  zipCode: `${prefix}ZipCode`,
  phone: `${prefix}Phone`,
  fax: `${prefix}Fax`,
  licenseNumber: `${prefix}LicenseNumber`,
  contactPerson: `${prefix}ContactPerson`,
  contactEmail: `${prefix}ContactEmail`,
  accountManager: `${prefix}AccountManager`,
});

export const getDealerFieldIds = prefix => ({
  attendantCompanyId: `${prefix}AttendantCompanyId`,
  attendantCompanyLocationId: `${prefix}AttendantCompanyLocationId`,
  name: `${prefix}Name`,
  streetAddress: `${prefix}StreetAddress`,
  city: `${prefix}City`,
  state: `${prefix}State`,
  zipCode: `${prefix}ZipCode`,
  phone: `${prefix}Phone`,
  fax: `${prefix}Fax`,
  contactCategory: `${prefix}ContactCategory`,
  contactPerson: `${prefix}ContactPerson`,
  contactEmail: `${prefix}ContactEmail`,
  accountManager: `${prefix}AccountManager`,
  salespersonName: `${prefix}SalespersonName`,
  salespersonPhone: `${prefix}SalespersonPhone`,
  salespersonEmail: `${prefix}SalespersonEmail`,
  contractDate: `${prefix}ContractDate`,
});

export const getInvestorFieldIds = prefix => ({
  attendantCompanyId: `${prefix}AttendantCompanyId`,
  attendantCompanyLocationId: `${prefix}AttendantCompanyLocationId`,
  name: `${prefix}Name`,
  streetAddress: `${prefix}StreetAddress`,
  city: `${prefix}City`,
  state: `${prefix}State`,
  zipCode: `${prefix}ZipCode`,
  phone: `${prefix}Phone`,
  fax: `${prefix}Fax`,
  channel: `${prefix}Channel`,
  contactPerson: `${prefix}ContactPerson`,
  contactEmail: `${prefix}ContactEmail`,
});

export const MH_BROKER_PREFIX = 'mhBroker';
export const MH_DEALER_PREFIX = 'mhDealer';
export const MH_INVESTOR_PREFIX = 'mhInvestor';

// Only the fields `buildManufacturedHomeContactPayloadEntry` actually persists
// (see Content.manufacturedHomeContacts.utils.js) belong here as editable — Fax
// has no backing field on the closing-attendant-company payload, so it is
// rendered separately via getFaxField and is never made editable.
const getContactDetailFields = (fieldId, { disabled }) => [
  {
    fieldId: fieldId.streetAddress,
    type: FieldType.INPUT,
    nameInModel: fieldId.streetAddress,
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'Physical Address',
      variant: 'outlined',
      disabled,
      required: false,
      widthInColumns: 1,
    },
  },
  {
    fieldId: fieldId.city,
    type: FieldType.INPUT,
    nameInModel: fieldId.city,
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'City',
      variant: 'outlined',
      disabled,
      required: false,
      widthInColumns: 1,
    },
  },
  {
    fieldId: fieldId.state,
    type: FieldType.INPUT,
    nameInModel: fieldId.state,
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'State',
      variant: 'outlined',
      disabled,
      required: false,
      widthInColumns: 1,
    },
  },
  {
    fieldId: fieldId.zipCode,
    type: FieldType.INPUT,
    nameInModel: fieldId.zipCode,
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'Zip',
      variant: 'outlined',
      disabled,
      required: false,
      widthInColumns: 1,
    },
  },
  {
    fieldId: fieldId.phone,
    type: FieldType.TEL,
    nameInModel: fieldId.phone,
    noValidate: true,
    inputProps: {
      type: FieldInputType.TEL_EXT,
      label: 'Telephone Number',
      variant: 'outlined',
      disabled,
      widthInColumns: 1,
    },
  },
];

// Fax has no backing field on `LoanClosingAttendantCompanyLocationRequestDto`, so
// it never persists regardless of section — always render it read-only.
const getFaxField = fieldId => [
  {
    fieldId: fieldId.fax,
    type: FieldType.INPUT,
    nameInModel: fieldId.fax,
    noValidate: true,
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'Fax',
      variant: 'outlined',
      disabled: true,
      required: false,
      widthInColumns: 1,
    },
  },
];

const getContactPersonFields = (fieldId, { disabled }) => [
  {
    fieldId: fieldId.contactPerson,
    type: FieldType.INPUT,
    nameInModel: fieldId.contactPerson,
    noValidate: true,
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'Contact Name',
      variant: 'outlined',
      disabled,
      required: false,
      widthInColumns: 1,
    },
  },
  {
    fieldId: fieldId.contactEmail,
    type: FieldType.INPUT,
    nameInModel: fieldId.contactEmail,
    inputProps: {
      type: FieldInputType.EMAIL,
      label: 'Email Address',
      variant: 'outlined',
      disabled,
      required: false,
      widthInColumns: 1,
    },
  },
];

const getBrokerSectionGroups = ({ brokerOptions, onBrokerChange, hasBroker }) => {
  const fieldId = getBrokerFieldIds(MH_BROKER_PREFIX);

  return [
    {
      type: FieldType.GROUP,
      key: 'mh-broker-info',
      groupTitle: {
        label: 'Broker Information',
        icon: <DocumentCopyIcon size={IconSize[16]} />,
        className: GROUP_TITLE_CLASS_NAME,
      },
      fields: [
        {
          fieldId: fieldId.attendantCompanyId,
          type: FieldType.SELECT,
          nameInModel: fieldId.attendantCompanyId,
          inputProps: {
            label: 'Broker Name',
            placeholder: 'Select broker…',
            variant: 'outlined',
            options: brokerOptions,
            widthInColumns: 1,
            required: hasBroker,
            customOnChange: onBrokerChange,
          },
        },
        ...getConditionalArray(hasBroker, () => [
          ...getContactDetailFields(fieldId, { disabled: true }),
          ...getFaxField(fieldId),
          {
            fieldId: fieldId.licenseNumber,
            type: FieldType.INPUT,
            nameInModel: fieldId.licenseNumber,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Broker Company NMLS #',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
          ...getContactPersonFields(fieldId, { disabled: true }),
          {
            fieldId: fieldId.accountManager,
            type: FieldType.INPUT,
            nameInModel: fieldId.accountManager,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Account Manager',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
        ]),
      ],
    },
  ];
};

const getDealerSectionGroups = ({ dealerOptions, onDealerChange, hasDealer }) => {
  const fieldId = getDealerFieldIds(MH_DEALER_PREFIX);

  return [
    {
      type: FieldType.GROUP,
      key: 'mh-dealer-info',
      groupTitle: {
        label: 'Dealer Information',
        icon: <BuildingIcon size={IconSize[16]} />,
        className: GROUP_TITLE_CLASS_NAME,
      },
      fields: [
        {
          fieldId: fieldId.attendantCompanyId,
          type: FieldType.SELECT,
          nameInModel: fieldId.attendantCompanyId,
          inputProps: {
            label: 'Dealer DBA Name',
            placeholder: 'Select dealer…',
            variant: 'outlined',
            options: dealerOptions,
            widthInColumns: 1,
            required: hasDealer,
            customOnChange: onDealerChange,
          },
        },
        ...getConditionalArray(hasDealer, () => [
          ...getContactDetailFields(fieldId, { disabled: false }),
          ...getFaxField(fieldId),
          {
            fieldId: fieldId.contactCategory,
            type: FieldType.INPUT,
            nameInModel: fieldId.contactCategory,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Contact Category',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
          ...getContactPersonFields(fieldId, { disabled: false }),
          {
            fieldId: fieldId.contractDate,
            type: FieldType.DATE,
            nameInModel: fieldId.contractDate,
            inputProps: {
              label: 'Contract Date',
              variant: 'outlined',
              format: COMMON_DATE_FORMAT,
              required: false,
              clearable: true,
              widthInColumns: 1,
            },
          },
          // Account Manager has no backing field on the closing-attendant-company
          // payload — never persisted, so it stays read-only like Contact Category.
          {
            fieldId: fieldId.accountManager,
            type: FieldType.INPUT,
            nameInModel: fieldId.accountManager,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Account Manager',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
        ]),
      ],
    },
    ...getConditionalArray(hasDealer, () => [
      {
        type: FieldType.GROUP,
        key: 'mh-dealer-salesperson',
        groupTitle: {
          label: 'Salesperson',
          className: GROUP_TITLE_CLASS_NAME,
        },
        // Salesperson name/phone/email have no backing field on the closing-
        // attendant-company payload (only a single `salesperson` catalog string
        // exists) — rendered read-only so nothing typed here is silently dropped.
        fields: [
          {
            fieldId: fieldId.salespersonName,
            type: FieldType.INPUT,
            nameInModel: fieldId.salespersonName,
            noValidate: true,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Salesperson Name',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.salespersonPhone,
            type: FieldType.TEL,
            nameInModel: fieldId.salespersonPhone,
            noValidate: true,
            inputProps: {
              type: FieldInputType.TEL_EXT,
              label: 'Phone Number',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
          {
            fieldId: fieldId.salespersonEmail,
            type: FieldType.INPUT,
            nameInModel: fieldId.salespersonEmail,
            inputProps: {
              type: FieldInputType.EMAIL,
              label: 'Email',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
        ],
      },
    ]),
  ];
};

const getInvestorSectionGroups = ({ investorOptions, onInvestorChange, hasInvestor }) => {
  const fieldId = getInvestorFieldIds(MH_INVESTOR_PREFIX);

  return [
    {
      type: FieldType.GROUP,
      key: 'mh-investor-info',
      groupTitle: {
        label: 'Investor Information',
        icon: <BankIcon size={IconSize[16]} />,
        className: GROUP_TITLE_CLASS_NAME,
      },
      fields: [
        {
          fieldId: fieldId.attendantCompanyId,
          type: FieldType.SELECT,
          nameInModel: fieldId.attendantCompanyId,
          inputProps: {
            label: 'Investor Name',
            placeholder: 'Select investor…',
            variant: 'outlined',
            options: investorOptions,
            widthInColumns: 1,
            required: hasInvestor,
            customOnChange: onInvestorChange,
          },
        },
        ...getConditionalArray(hasInvestor, () => [
          ...getContactDetailFields(fieldId, { disabled: true }),
          ...getFaxField(fieldId),
          {
            fieldId: fieldId.channel,
            type: FieldType.INPUT,
            nameInModel: fieldId.channel,
            inputProps: {
              type: FieldInputType.TEXT,
              label: 'Channel',
              variant: 'outlined',
              disabled: true,
              required: false,
              widthInColumns: 1,
            },
          },
          ...getContactPersonFields(fieldId, { disabled: true }),
        ]),
      ],
    },
  ];
};

export const getManufacturedHomeContactsFormConfig = ({
  brokerOptions = [],
  dealerOptions = [],
  investorOptions = [],
  onBrokerChange,
  onDealerChange,
  onInvestorChange,
  hasBroker = false,
  hasDealer = false,
  hasInvestor = false,
} = {}) => ({
  groups: [
    {
      type: FieldType.GROUP,
      key: 'mh-contacts-banner',
      fields: [],
      additionalGroupContentConfig: {
        wrapperStyles: fullWidthWrapperStyles,
        fields: [{ component: ManufacturedHomeContactsBanner }],
      },
    },
    ...getBrokerSectionGroups({ brokerOptions, onBrokerChange, hasBroker }),
    ...getDealerSectionGroups({ dealerOptions, onDealerChange, hasDealer }),
    ...getInvestorSectionGroups({ investorOptions, onInvestorChange, hasInvestor }),
  ],
});
